import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { isSpenderOnlyRole } from '@ankor-io/common/domain/iam/account'
import type { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import type { ReceiptCaptureDraftPayload } from '@ankor-io/common/types/receipt-capture'
import type {
  ConfirmTransactionAttachmentUploadRequest,
  CreateOpsManualTransactionBody,
  CreateTransactionAttachmentBody,
  FullTransactionPayload,
  ListTransactionsRequest,
  PaymentMethod,
  PrepareTransactionAttachmentUploadRequest,
  TransactionAttachmentPayload,
  TransactionPayload,
  TransactionStatus,
  UpdateAnkorPayTransactionBody,
  UpdateManualTransactionBody,
} from '@ankor-io/common/types/transaction/transaction'

import { transactionService } from '@/api/services/transaction.service'
import { useUserContext } from '@/providers/user/useUserContext'

import { invalidateBoatCashFloatQueries, invalidateBoatCashFloatQueriesForTransaction } from './cashFloatInvalidation'
import { validateContextIds, validateTransactionAttachmentQueryParams, validateTransactionQueryParams } from './helpers'

export type ReceiptCaptureMutationVariables = {
  file: File
  awxConnectedAccountType: AwxConnectedAccountType
}

/**
 * TanStack Infinite Query hook for fetching transactions list with infinite scroll.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @param request - Base request parameters (filters only, skip will be managed by pageParam)
 * @param pageSize - Number of transactions to fetch per page (default: 20)
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns Infinite query result with pages of transactions, loading state, and fetchNextPage function
 *
 * @example
 * ```ts
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage
 * } = useListTransactionsQuery({
 *   status: [TransactionStatus.APPROVED]
 * }, 20)
 *
 * // Access all transactions across pages
 * const allTransactions = data?.pages.flat() ?? []
 * ```
 */
export function useListTransactionsQuery(
  request: Omit<ListTransactionsRequest, 'skip' | 'take'> = {},
  pageSize = 20,
  enabled = true,
) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  const isEnabled =
    enabled && Boolean(activeOrganizationId) && Boolean(activeVesselId) && Boolean(activeConnectedAccountId)

  return useInfiniteQuery<TransactionPayload[], Error>({
    queryKey: ['transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId, request, pageSize],
    queryFn: ({ pageParam = 0 }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.listTransactions(orgId, vesselId, connectedAccountId, {
        ...request,
        skip: pageParam as number,
        take: pageSize,
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer items than pageSize, we've reached the end
      if (lastPage.length < pageSize) {
        return undefined
      }

      // Return the skip value for the next page
      return allPages.length * pageSize
    },
    initialPageParam: 0,
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching my transactions list.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: transactions, isLoading, error } = useListMyTransactionsQuery({ take: 10, skip: 0 })
 * ```
 */
export function useListMyTransactionsQuery(params?: ListTransactionsRequest & { enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { enabled = true, ...listParams } = params || {}

  const isEnabled =
    enabled && Boolean(activeOrganizationId) && Boolean(activeVesselId) && Boolean(activeConnectedAccountId)

  return useQuery<TransactionPayload[]>({
    queryKey: ['my-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId, listParams],
    queryFn: () => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.listMyTransactions(orgId, vesselId, connectedAccountId, listParams)
    },
    enabled: isEnabled,
  })
}

/**
 * Infinite-query variant of useListMyTransactionsQuery. Returns only the current
 * user's transactions. Used by the Expenses page when the active role is SPENDER.
 *
 * Uses a distinct query key (['my-transactions-infinite', …]) so it never collides
 * with the all-transactions infinite key or the legacy non-infinite my-transactions key.
 */
export function useListMyTransactionsInfiniteQuery(
  request: Omit<ListTransactionsRequest, 'skip' | 'take'> = {},
  pageSize = 20,
  enabled = true,
) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  const isEnabled =
    enabled && Boolean(activeOrganizationId) && Boolean(activeVesselId) && Boolean(activeConnectedAccountId)

  return useInfiniteQuery<TransactionPayload[], Error>({
    queryKey: [
      'my-transactions-infinite',
      activeOrganizationId,
      activeVesselId,
      activeConnectedAccountId,
      request,
      pageSize,
    ],
    queryFn: ({ pageParam = 0 }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.listMyTransactions(orgId, vesselId, connectedAccountId, {
        ...request,
        skip: pageParam as number,
        take: pageSize,
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < pageSize) {
        return undefined
      }
      return allPages.length * pageSize
    },
    initialPageParam: 0,
    enabled: isEnabled,
  })
}

/**
 * Role-aware dispatcher for the Expenses page list.
 * - SPENDER → useListMyTransactionsInfiniteQuery (only the current user's records).
 * - Any other role → useListTransactionsQuery (all records on the connected account).
 *
 * Both branches share the same useInfiniteQuery result shape, so call sites
 * consume identically.
 */
export function useListTransactionsForCurrentRoleQuery(
  request: Omit<ListTransactionsRequest, 'skip' | 'take'> = {},
  pageSize = 20,
  enabled = true,
) {
  const { activeOrganizationRole } = useUserContext()
  const isSpender = isSpenderOnlyRole(activeOrganizationRole)

  const myResult = useListMyTransactionsInfiniteQuery(request, pageSize, enabled && isSpender)
  const allResult = useListTransactionsQuery(request, pageSize, enabled && !isSpender)

  return isSpender ? myResult : allResult
}

/**
 * TanStack Query hook for fetching a single transaction by ID.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: transaction, isLoading, error } = useGetTransactionByIdQuery({ transactionId: 'txn_123' })
 * ```
 */
export function useGetTransactionByIdQuery(params: { transactionId: string; enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { transactionId, enabled = true } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(transactionId)

  return useQuery<FullTransactionPayload>({
    queryKey: ['transaction', activeOrganizationId, activeVesselId, activeConnectedAccountId, transactionId],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        transactionId: validatedTransactionId,
      } = validateTransactionQueryParams(activeOrganizationId, activeVesselId, activeConnectedAccountId, transactionId)

      return transactionService.getTransactionById(orgId, vesselId, connectedAccountId, validatedTransactionId)
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching transaction attachments.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: attachments, isLoading, error } = useTransactionAttachmentsQuery({
 *   transactionId: 'txn_123',
 *   take: 10,
 *   skip: 0
 * })
 * ```
 */
export function useTransactionAttachmentsQuery(params: {
  transactionId: string
  take?: number
  skip?: number
  enabled?: boolean
}) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { transactionId, enabled = true, ...attachmentParams } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(transactionId)

  return useQuery<TransactionAttachmentPayload[]>({
    queryKey: [
      'transaction-attachments',
      activeOrganizationId,
      activeVesselId,
      activeConnectedAccountId,
      transactionId,
      attachmentParams,
    ],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        transactionId: validatedTransactionId,
      } = validateTransactionQueryParams(activeOrganizationId, activeVesselId, activeConnectedAccountId, transactionId)

      return transactionService.listTransactionAttachments(
        orgId,
        vesselId,
        connectedAccountId,
        validatedTransactionId,
        attachmentParams,
      )
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query hook for fetching a transaction attachment by ID.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const { data: attachment, isLoading, error } = useGetTransactionAttachmentByIdQuery({
 *   transactionId: 'txn_123',
 *   attachmentId: 'att_456'
 * })
 * ```
 */
export function useGetTransactionAttachmentByIdQuery(params: {
  transactionId: string
  attachmentId: string
  enabled?: boolean
}) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const { transactionId, attachmentId, enabled = true } = params

  const isEnabled =
    enabled &&
    Boolean(activeOrganizationId) &&
    Boolean(activeVesselId) &&
    Boolean(activeConnectedAccountId) &&
    Boolean(transactionId) &&
    Boolean(attachmentId)

  return useQuery<TransactionAttachmentPayload>({
    queryKey: [
      'transaction-attachment',
      activeOrganizationId,
      activeVesselId,
      activeConnectedAccountId,
      transactionId,
      attachmentId,
    ],
    queryFn: () => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        transactionId: validatedTransactionId,
        attachmentId: validatedAttachmentId,
      } = validateTransactionAttachmentQueryParams(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        transactionId,
        attachmentId,
      )

      return transactionService.getTransactionAttachmentById(
        orgId,
        vesselId,
        connectedAccountId,
        validatedTransactionId,
        validatedAttachmentId,
      )
    },
    enabled: isEnabled,
  })
}

// ========== Mutation Hooks ==========

/**
 * TanStack Query mutation hook for updating a transaction.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const updateTransaction = useUpdateTransactionMutation()
 * updateTransaction.mutate({
 *   transactionId: 'txn_123',
 *   payload: { vendor: 'New Vendor', charterSelected: true }
 * })
 * ```
 */
export function useUpdateTransactionMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      transactionId,
      payload,
    }: {
      transactionId: string
      payload: UpdateManualTransactionBody | UpdateAnkorPayTransactionBody
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.updateTransaction(orgId, vesselId, connectedAccountId, transactionId, payload)
    },
    onSuccess: (data, variables) => {
      const cachedTransaction = queryClient.getQueryData<TransactionPayload>([
        'transaction',
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        variables.transactionId,
      ])

      // Invalidate transaction list queries
      queryClient.invalidateQueries({
        queryKey: ['transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions list queries
      queryClient.invalidateQueries({
        queryKey: ['my-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions infinite list queries (used by spender role)
      queryClient.invalidateQueries({
        queryKey: ['my-transactions-infinite', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate specific transaction query
      queryClient.invalidateQueries({
        queryKey: [
          'transaction',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
        ],
      })

      invalidateBoatCashFloatQueriesForTransaction({
        queryClient,
        organizationId: activeOrganizationId,
        vesselId: activeVesselId,
        transaction: cachedTransaction,
      })
      invalidateBoatCashFloatQueriesForTransaction({
        queryClient,
        organizationId: activeOrganizationId,
        vesselId: activeVesselId,
        transaction: data,
      })
    },
  })
}

/**
 * TanStack Query mutation hook for updating an OPS transaction.
 * Uses the module-specific `/ops/transaction/:transactionId` endpoint.
 */
export function useUpdateOpsTransactionMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      transactionId,
      payload,
    }: {
      transactionId: string
      payload: UpdateManualTransactionBody | UpdateAnkorPayTransactionBody
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.updateOpsTransaction(orgId, vesselId, connectedAccountId, transactionId, payload)
    },
    onSuccess: (data, variables) => {
      const cachedTransaction = queryClient.getQueryData<TransactionPayload>([
        'transaction',
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        variables.transactionId,
      ])

      // Invalidate transaction list queries
      queryClient.invalidateQueries({
        queryKey: ['transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions list queries
      queryClient.invalidateQueries({
        queryKey: ['my-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions infinite list queries (used by spender role)
      queryClient.invalidateQueries({
        queryKey: ['my-transactions-infinite', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate specific transaction query
      queryClient.invalidateQueries({
        queryKey: [
          'transaction',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
        ],
      })

      invalidateBoatCashFloatQueriesForTransaction({
        queryClient,
        organizationId: activeOrganizationId,
        vesselId: activeVesselId,
        transaction: cachedTransaction,
      })
      invalidateBoatCashFloatQueriesForTransaction({
        queryClient,
        organizationId: activeOrganizationId,
        vesselId: activeVesselId,
        transaction: data,
      })
    },
  })
}

/**
 * TanStack Query mutation hook for updating transaction status.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const updateStatus = useUpdateTransactionStatusMutation()
 * updateStatus.mutate({
 *   transactionId: 'txn_123',
 *   status: TransactionStatus.APPROVED
 * })
 * ```
 */
export function useUpdateTransactionStatusMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      transactionId,
      status,
    }: {
      transactionId: string
      status: TransactionStatus
      paymentMethod?: PaymentMethod
      cashFloatId?: string
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.updateTransactionStatus(orgId, vesselId, connectedAccountId, transactionId, status)
    },
    onSuccess: (_data, variables) => {
      const cachedTransaction = queryClient.getQueryData<TransactionPayload>([
        'transaction',
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        variables.transactionId,
      ])

      // Invalidate transaction list queries
      queryClient.invalidateQueries({
        queryKey: ['transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions list queries
      queryClient.invalidateQueries({
        queryKey: ['my-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions infinite list queries (used by spender role)
      queryClient.invalidateQueries({
        queryKey: ['my-transactions-infinite', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate specific transaction query
      queryClient.invalidateQueries({
        queryKey: [
          'transaction',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
        ],
      })

      // Invalidate apa-owes-ops-transactions query
      queryClient.invalidateQueries({
        queryKey: ['apa-owes-ops-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Charter detail expenses + stats (partial keys — any charter / filters / pagination)
      queryClient.invalidateQueries({
        queryKey: ['charter-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      queryClient.invalidateQueries({
        queryKey: ['charter-stats', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      invalidateBoatCashFloatQueries({
        queryClient,
        organizationId: activeOrganizationId,
        vesselId: activeVesselId,
        paymentMethod: variables.paymentMethod ?? cachedTransaction?.paymentMethod,
        cashFloatId: variables.cashFloatId ?? cachedTransaction?.cashFloat?.id,
      })
    },
  })
}

/**
 * TanStack Query mutation hook for deleting a transaction.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const deleteTransaction = useDeleteTransactionMutation()
 * deleteTransaction.mutate({ transactionId: 'txn_123' })
 * ```
 */
export function useDeleteTransactionMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      transactionId,
    }: {
      transactionId: string
      paymentMethod?: PaymentMethod
      cashFloatId?: string
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.deleteTransaction(orgId, vesselId, connectedAccountId, transactionId)
    },
    onSuccess: (_data, variables) => {
      const cachedTransaction = queryClient.getQueryData<TransactionPayload>([
        'transaction',
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        variables.transactionId,
      ])

      // Invalidate transaction list queries
      queryClient.invalidateQueries({
        queryKey: ['transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions list queries
      queryClient.invalidateQueries({
        queryKey: ['my-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      // Invalidate my transactions infinite list queries (used by spender role)
      queryClient.invalidateQueries({
        queryKey: ['my-transactions-infinite', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      queryClient.invalidateQueries({
        queryKey: [
          'transaction',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
        ],
      })

      queryClient.invalidateQueries({
        queryKey: [
          'transaction-attachments',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
        ],
      })

      queryClient.invalidateQueries({
        queryKey: ['apa-owes-ops-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      queryClient.invalidateQueries({
        queryKey: ['charter-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })
      queryClient.invalidateQueries({
        queryKey: ['charter-stats', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })

      invalidateBoatCashFloatQueries({
        queryClient,
        organizationId: activeOrganizationId,
        vesselId: activeVesselId,
        paymentMethod: variables.paymentMethod ?? cachedTransaction?.paymentMethod,
        cashFloatId: variables.cashFloatId ?? cachedTransaction?.cashFloat?.id,
      })
    },
  })
}

/**
 * TanStack Query mutation hook for creating a transaction attachment.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const createAttachment = useCreateTransactionAttachmentMutation()
 * createAttachment.mutate({
 *   transactionId: 'txn_123',
 *   payload: { name: 'receipt.pdf', mimeType: 'application/pdf' }
 * })
 * ```
 */
export function useCreateTransactionAttachmentMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      transactionId,
      payload,
    }: {
      transactionId: string
      payload: CreateTransactionAttachmentBody
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.createTransactionAttachment(orgId, vesselId, connectedAccountId, transactionId, payload)
    },
    onSuccess: (_data, variables) => {
      // Invalidate transaction attachments list
      queryClient.invalidateQueries({
        queryKey: [
          'transaction-attachments',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
        ],
      })
    },
  })
}

/**
 * TanStack Query mutation hook for preparing transaction attachment upload.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 *
 * @example
 * ```ts
 * const prepareUpload = usePrepareTransactionAttachmentUploadMutation()
 * prepareUpload.mutate({
 *   transactionId: 'txn_123',
 *   payload: { name: 'receipt.pdf', mimeType: 'application/pdf', transactionId: 'txn_123', uploadedBy: 'user_123' }
 * })
 * ```
 */
export function usePrepareTransactionAttachmentUploadMutation() {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      transactionId,
      payload,
    }: {
      transactionId: string
      payload: PrepareTransactionAttachmentUploadRequest
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.prepareTransactionAttachmentUpload(
        orgId,
        vesselId,
        connectedAccountId,
        transactionId,
        payload,
      )
    },
  })
}

/**
 * TanStack Query mutation hook for confirming transaction attachment upload.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const confirmUpload = useConfirmTransactionAttachmentUploadMutation()
 * confirmUpload.mutate({
 *   transactionId: 'txn_123',
 *   payload: { uploadToken: 'token_123' }
 * })
 * ```
 */
export function useConfirmTransactionAttachmentUploadMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      transactionId,
      payload,
    }: {
      transactionId: string
      payload: ConfirmTransactionAttachmentUploadRequest
    }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.confirmTransactionAttachmentUpload(
        orgId,
        vesselId,
        connectedAccountId,
        transactionId,
        payload,
      )
    },
    onSuccess: (_data, variables) => {
      // Invalidate transaction attachments list
      queryClient.invalidateQueries({
        queryKey: [
          'transaction-attachments',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
        ],
      })
    },
  })
}

/**
 * TanStack Query hook for tax labels available on the active OPS connected account (manual expense picker).
 */
export function useListTaxLabelsForConnectedAccountQuery(options?: { enabled?: boolean }) {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()
  const enabled = options?.enabled ?? true

  return useQuery({
    queryKey: ['tax-labels', activeOrganizationId, activeVesselId, activeConnectedAccountId],
    queryFn: async () => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.listTaxLabelsForConnectedAccount(orgId, vesselId, connectedAccountId)
    },
    enabled: Boolean(activeOrganizationId && activeVesselId && activeConnectedAccountId && enabled),
  })
}

/**
 * TanStack Query mutation hook for creating an ops (operations) manual transaction.
 * Automatically uses activeOrganizationId, activeVesselId, and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const createTransaction = useCreateOpsTransactionMutation()
 * createTransaction.mutate({
 *   payload: {
 *     categoryId: '…',
 *     taxLabelId: '…',
 *     originalAmountCents: 10000,
 *     originalAmountCurrencyCode: 'USD',
 *     paymentMethod: PaymentMethod.BOAT_CASH,
 *     date: new Date().toISOString(),
 *   },
 * })
 * ```
 */
export function useCreateOpsTransactionMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({ payload }: { payload: CreateOpsManualTransactionBody }) => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.createOpsTransaction(orgId, vesselId, connectedAccountId, payload)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })
      queryClient.invalidateQueries({
        queryKey: ['my-transactions', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })
      // Invalidate my transactions infinite list queries (used by spender role)
      queryClient.invalidateQueries({
        queryKey: ['my-transactions-infinite', activeOrganizationId, activeVesselId, activeConnectedAccountId],
      })
      invalidateBoatCashFloatQueries({
        queryClient,
        organizationId: activeOrganizationId,
        vesselId: activeVesselId,
        paymentMethod: variables.payload.paymentMethod,
        cashFloatId: variables.payload.cashFloatId,
      })
    },
  })
}

/**
 * Receipt capture OCR for manual expense prefill. Uses org / vessel / connected account from context.
 */
export function useReceiptCaptureMutation() {
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({
      file,
      awxConnectedAccountType,
    }: ReceiptCaptureMutationVariables): Promise<ReceiptCaptureDraftPayload> => {
      const { orgId, vesselId, connectedAccountId } = validateContextIds(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
      )
      return transactionService.captureReceiptDraft(orgId, vesselId, connectedAccountId, awxConnectedAccountType, file)
    },
  })
}

/**
 * TanStack Query mutation hook for deleting a transaction attachment.
 * Automatically uses activeOrganizationId and activeConnectedAccountId from context.
 * Invalidates relevant queries after successful mutation.
 *
 * @example
 * ```ts
 * const deleteAttachment = useDeleteTransactionAttachmentMutation()
 * deleteAttachment.mutate({
 *   transactionId: 'txn_123',
 *   attachmentId: 'att_456'
 * })
 * ```
 */
export function useDeleteTransactionAttachmentMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId, activeVesselId, activeConnectedAccountId } = useUserContext()

  return useMutation({
    mutationFn: async ({ transactionId, attachmentId }: { transactionId: string; attachmentId: string }) => {
      const {
        orgId,
        vesselId,
        connectedAccountId,
        transactionId: validatedTransactionId,
        attachmentId: validatedAttachmentId,
      } = validateTransactionAttachmentQueryParams(
        activeOrganizationId,
        activeVesselId,
        activeConnectedAccountId,
        transactionId,
        attachmentId,
      )

      return transactionService.deleteTransactionAttachment(
        orgId,
        vesselId,
        connectedAccountId,
        validatedTransactionId,
        validatedAttachmentId,
      )
    },
    onSuccess: (_data, variables) => {
      // Invalidate transaction attachments list
      queryClient.invalidateQueries({
        queryKey: [
          'transaction-attachments',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
        ],
      })

      // Invalidate specific attachment query
      queryClient.invalidateQueries({
        queryKey: [
          'transaction-attachment',
          activeOrganizationId,
          activeVesselId,
          activeConnectedAccountId,
          variables.transactionId,
          variables.attachmentId,
        ],
      })
    },
  })
}
